import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WhatsappLogo, Phone, EnvelopeSimple, MapPin } from "@phosphor-icons/react";
import { SERVICES, DOWNLOADS } from "@/lib/content";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${BACKEND}/api/enquiries`, form);
      toast.success("Enquiry received. Pankaj will get back within 24 hrs.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch { toast.error("Something went wrong. Try WhatsApp?"); }
    setBusy(false);
  };
  return (
    <>
      <section className="bg-[var(--pta-cream)] pt-16 md:pt-24 pb-16">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="chapter-num">Contact</div>
            <h1 className="font-serif-display text-6xl md:text-8xl text-[var(--pta-navy)] mt-4 leading-[0.95]">Let's talk<br /><em className="italic text-[var(--pta-gold)]">numbers.</em></h1>
            <p className="mt-8 text-lg text-[var(--pta-ink)]/75 max-w-xl">Fill the form or pick the fastest lane below. We respond within 24 hours on business days.</p>
            <form onSubmit={submit} className="mt-10 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div><Label className="overline">Name</Label><Input data-testid="contact-name" required value={form.name} onChange={(e)=>set('name',e.target.value)} className="mt-2" /></div>
                <div><Label className="overline">Email</Label><Input data-testid="contact-email" required type="email" value={form.email} onChange={(e)=>set('email',e.target.value)} className="mt-2" /></div>
                <div><Label className="overline">Phone</Label><Input data-testid="contact-phone" required value={form.phone} onChange={(e)=>set('phone',e.target.value)} className="mt-2" /></div>
                <div><Label className="overline">Service</Label>
                  <Select value={form.service} onValueChange={(v)=>set('service',v)}>
                    <SelectTrigger data-testid="contact-service" className="mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{SERVICES.map(s=><SelectItem key={s.slug} value={s.title}>{s.title}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="overline">Message</Label><Textarea data-testid="contact-message" required value={form.message} onChange={(e)=>set('message',e.target.value)} rows={5} className="mt-2" /></div>
              <button data-testid="contact-submit" type="submit" disabled={busy} className="btn-pta">{busy?"Sending…":"Send Enquiry"}</button>
            </form>
          </div>
          <aside className="lg:col-span-5 space-y-6">
            <div className="border hairline-navy p-6">
              <div className="overline">Direct lines</div>
              <div className="mt-4 divide-y hairline">
                <a data-testid="contact-phone-link" href="tel:+919560029986" className="flex items-center gap-3 py-4 group"><Phone size={18} weight="bold" className="text-[var(--pta-gold)]" /><span className="font-serif-display text-xl text-[var(--pta-navy)] group-hover:text-[var(--pta-gold)]">+91 9560 029 986</span></a>
                <a data-testid="contact-email-link" href="mailto:Pankaj.talan@MukMan.com" className="flex items-center gap-3 py-4 group"><EnvelopeSimple size={18} weight="bold" className="text-[var(--pta-gold)]" /><span className="font-serif-display text-base md:text-lg text-[var(--pta-navy)] group-hover:text-[var(--pta-gold)]">Pankaj.talan@MukMan.com</span></a>
                <a data-testid="contact-wa-link" href="https://wa.me/919560029986" target="_blank" rel="noreferrer" className="flex items-center gap-3 py-4 group"><WhatsappLogo size={18} weight="bold" className="text-[var(--pta-gold)]" /><span className="font-serif-display text-xl text-[var(--pta-navy)] group-hover:text-[var(--pta-gold)]">Chat on WhatsApp</span></a>
                <div className="flex items-center gap-3 py-4"><MapPin size={18} weight="bold" className="text-[var(--pta-gold)]" /><span className="text-[var(--pta-navy)]">Delhi NCR, India</span></div>
              </div>
            </div>
            <div className="border hairline-navy p-6">
              <div className="overline">Free downloads</div>
              <div className="mt-4 space-y-3">
                {DOWNLOADS.map((d,i)=>(
                  <div key={i} data-testid={`download-${i}`} className="border-b hairline pb-3 last:border-b-0">
                    <div className="font-serif-display text-lg text-[var(--pta-navy)]">{d.title}</div>
                    <div className="text-xs text-[var(--pta-ink)]/60">{d.desc}</div>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-xs text-[var(--pta-ink)]/50 font-mono-fine tracking-widest uppercase">Sent on request — email us</p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
